<template>
    <RegularModal ref="inputModal" modal-card-class="is-large" :prevent-close-by-background="true">
        <template v-slot:title> {{ translate("admin_fights_manage.edit_fights_order") }} {{ mat?.name }} </template>
        <template v-slot:body>
            <table class="table is-fullwidth is-hoverable is-condensed" @mouseleave="fightHover(0)">
                <Sortable tag="tbody" :list="internalFights" item-key="id" :options="dragOptions" @update="onTableUpdate">
                    <template v-slot:item="{ element: fight, index }">
                        <FightTableRow
                            class="draggable-table-item"
                            :fight="fight"
                            :show-duplicated-pair="false"
                            :index="index"
                            @fightHover="fightHover"
                            :highlight-parameter-id="highlightParameterId"
                        />
                    </template>
                </Sortable>
            </table>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button @click="close" class="button">
                    {{ translate("common.cancel") }}
                </button>
                <button @click="confirm" class="button is-primary">
                    {{ translate("common.ok") }}
                </button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import FightTableRow from "./FightTableRow.vue";
    import { Sortable } from "sortablejs-vue3";
    import RegularModal from "../Common/RegularModal.vue";
    import cloneDeep from "lodash.clonedeep";
    import { translate } from "../Common/translations";
    import { OrganizerComplexFight } from "../Common/Fights/types";
    import { Mat } from "../Common/types";
    import { adaptToSortableUpdate, SortableEvent } from "../Common/sortableUtils";

    const internalFights = ref<OrganizerComplexFight[]>([]);
    const mat = ref<Mat | null>(null);
    const highlightParameterId = ref(0);
    const dragOptions = {
        animation: 150,
        scroll: true,
        forceFallback: true,
        bubbleScroll: true,
    };

    const emit = defineEmits(["onReorderedFights"]);

    function fightHover(parameterId) {
        highlightParameterId.value = parameterId;
    }

    function confirm() {
        const fightIds = internalFights.value.map(fight => fight.id);
        emit("onReorderedFights", mat.value?.id, fightIds);
        close();
    }

    const inputModal = ref<InstanceType<typeof RegularModal> | null>(null);

    function open(fights: OrganizerComplexFight[], matData: Mat) {
        internalFights.value = cloneDeep(fights);
        mat.value = matData;
        inputModal.value?.open();
    }

    function close() {
        inputModal.value?.close();
    }

    defineExpose({ open, close });

    function onTableUpdate(event: SortableEvent) {
        internalFights.value = adaptToSortableUpdate(event, internalFights.value);
    }
</script>

<style>
    .draggable-table-item {
        cursor: move;
    }
</style>
